/* eslint-disable react/jsx-max-props-per-line */
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import fileDownload from 'js-file-download';
import { Alert, Box, Button, CircularProgress, DialogContentText, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import { DialogDefaultV2, WidgetV2 } from '../../../../components/v2';
import PdfViewerDialogV2 from '../../../../components/v2/filePreview/PdfViewerDialogV2';
import * as Status from '../../../../constants/status';
import * as actionsParseRfg from '../../../../store/parseRfg/actions';
import * as selectorsParseRfg from '../../../../store/parseRfg/reducer';
import * as actionsToast from '../../../../store/toast/actions';
import FileService from '../../../../services/FileService';
import { downloadPresignedUrl } from '../../../../helpers/downloadPresignedUrl';
import { PARSE_RFG_DOCUMENTO_PATH } from '../../../../helpers/auditoriaFaixa';
import BaseService from '../../../../services/BaseService';
import {
  RELATORIO_JOB_POLL_MS,
  RELATORIO_JOB_STATUS,
  getJobSubtitle,
  isRelatorioJobAvailable,
  isRelatorioJobInFlight,
} from '../../../../helpers/relatorioJob';

const ParseRfgPanel = ({
  nsirId,
  nsirStatus,
  canUpload,
  job,
  documento,
  uploading,
  conflict,
  startParseRfg,
  fetchParseRfgJob,
  fetchParseRfgDocumento,
  clearParseRfgConflict,
  messageError,
}) => {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [pdfOpen, setPdfOpen] = useState(false);
  const [pdfFile, setPdfFile] = useState(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  const fileInputRef = useRef(null);
  const lastFileRef = useRef(null);

  const success = isRelatorioJobAvailable(job && job.status);
  const busy = uploading || isRelatorioJobInFlight(job && job.status);
  const canMutate = canUpload && nsirStatus === Status.EM_CRIACAO && !busy;
  const showFirstUpload = canMutate && !success;
  const showReplace = canMutate && success;
  const pdfNome = (documento && documento.nome) || (job && job.nomeArquivo) || 'RFG.pdf';
  const pdfUrl = (documento && documento.url) || (job && job.url) || null;
  const jobId = job && job.id;

  useEffect(() => {
    if (!nsirId) return undefined;
    fetchParseRfgJob(nsirId, jobId);
    return undefined;
  }, [nsirId]);

  useEffect(() => {
    if (!nsirId || !job || !isRelatorioJobInFlight(job.status)) return undefined;
    const timer = window.setInterval(() => {
      fetchParseRfgJob(nsirId, job.id);
    }, RELATORIO_JOB_POLL_MS);
    return () => window.clearInterval(timer);
  }, [nsirId, job, fetchParseRfgJob]);

  useEffect(() => {
    if (success && jobId) fetchParseRfgDocumento(jobId);
  }, [success, jobId, fetchParseRfgDocumento]);

  useEffect(() => {
    if (!conflict) return;
    if (!pendingFile && lastFileRef.current) setPendingFile(lastFileRef.current);
    setConfirmOpen(true);
  }, [conflict]);

  const enqueueFile = (file) => {
    if (!file) return;
    lastFileRef.current = file;
    setPendingFile(file);
    startParseRfg(nsirId, file, false);
  };

  const onPickFile = (event) => {
    const file = event.target.files && event.target.files[0];
    event.target.value = '';
    enqueueFile(file);
  };

  const resolvePdf = async () => {
    if (pdfUrl) return { url: pdfUrl, nome: pdfNome };
    if (!jobId) return null;
    try {
      const response = await BaseService.get({ url: PARSE_RFG_DOCUMENTO_PATH(jobId) });
      const data = response.data || {};
      const url = data.url || data.arquivoUrl || (data.arquivo && data.arquivo.url);
      const nome = data.nome || data.nomeArquivo || (data.arquivo && data.arquivo.nome) || pdfNome;
      if (url) return { url, nome };
    } catch (error) {
      // job ainda PROCESSING ou PDF indisponível
    }
    return null;
  };

  const loadPdfBlob = async (url) => {
    const response = await FileService.getFile(url);
    return response.data;
  };

  const handleView = async () => {
    setPdfBusy(true);
    try {
      const resolved = await resolvePdf();
      if (!resolved) {
        messageError({ message: 'O PDF do RFG ainda não está disponível para visualização neste ambiente.' });
        return;
      }
      const blob = await loadPdfBlob(resolved.url);
      setPdfFile(blob);
      setPdfOpen(true);
    } catch (error) {
      const resolved = await resolvePdf();
      if (resolved) downloadPresignedUrl(resolved.url);
      else messageError({ message: 'Não foi possível abrir o PDF do RFG.' });
    } finally {
      setPdfBusy(false);
    }
  };

  const handleDownload = async () => {
    setPdfBusy(true);
    try {
      const resolved = await resolvePdf();
      if (!resolved) {
        messageError({ message: 'O PDF do RFG ainda não está disponível para download neste ambiente.' });
        return;
      }
      try {
        const blob = await loadPdfBlob(resolved.url);
        fileDownload(blob, resolved.nome);
      } catch (error) {
        downloadPresignedUrl(resolved.url);
      }
    } finally {
      setPdfBusy(false);
    }
  };

  const severity = () => {
    if (!job) return 'info';
    if (isRelatorioJobInFlight(job.status)) return 'info';
    if (job.status === RELATORIO_JOB_STATUS.ERROR) return 'error';
    if (success) return 'success';
    return 'info';
  };

  const headerAvatar = (busy || pdfBusy) ? (
    <CircularProgress size={18} thickness={5} sx={{ color: 'common.white' }} />
  ) : (
    <PictureAsPdfOutlinedIcon sx={{ fontSize: 22 }} />
  );

  return (
    <WidgetV2 title="Relatório fotográfico (RFG)" insideTab avatar={headerAvatar}>
      <Box sx={{ px: 1.5, py: 1 }}>
        {job && (
          <Alert
            severity={severity()}
            sx={{ mb: 1, py: 0, '& .MuiAlert-message': { py: 0.75, fontSize: 13 } }}
          >
            {getJobSubtitle(job)}
          </Alert>
        )}
        {!success && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            Envie o PDF da terceirizada. O parse pré-preenche a aba Serviços; se falhar, publique a inspeção e cadastre os pontos na mão.
          </Typography>
        )}
        <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
          {success && (
            <>
              <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
                {pdfNome}
              </Typography>
              <Tooltip title="Visualizar PDF">
                <span>
                  <IconButton size="small" onClick={handleView} disabled={pdfBusy} aria-label="Visualizar PDF">
                    <VisibilityOutlinedIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Baixar PDF">
                <span>
                  <IconButton size="small" onClick={handleDownload} disabled={pdfBusy} aria-label="Baixar PDF">
                    <DownloadOutlinedIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </>
          )}
          {(showFirstUpload || showReplace) && (
            <>
              <input
                ref={fileInputRef}
                type="file"
                accept="application/pdf,.pdf"
                hidden
                onChange={onPickFile}
              />
              <Button
                size="small"
                variant={showReplace ? 'outlined' : 'contained'}
                onClick={() => fileInputRef.current && fileInputRef.current.click()}
                disabled={!canMutate}
                sx={{ fontWeight: 600, minWidth: 'auto', px: 1.25 }}
              >
                {showReplace ? 'Substituir' : 'Enviar PDF'}
              </Button>
            </>
          )}
        </Stack>
      </Box>
      <DialogDefaultV2
        open={confirmOpen}
        title="Substituir o RFG?"
        confirmMessage="Substituir"
        handlerCancel={() => {
          setConfirmOpen(false);
          setPendingFile(null);
          if (conflict) clearParseRfgConflict();
        }}
        handlerConfirm={() => {
          const file = pendingFile || lastFileRef.current;
          const shouldForce = Boolean(conflict);
          setConfirmOpen(false);
          setPendingFile(null);
          if (conflict) clearParseRfgConflict();
          if (file && shouldForce) startParseRfg(nsirId, file, true);
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <WarningAmberOutlinedIcon color="warning" sx={{ fontSize: 28, flexShrink: 0 }} />
          <DialogContentText>
            {typeof conflict === 'string' ? conflict : 'Já existe um RFG importado para esta NSIR. Deseja substituir?'}
            {' '}
            A substituição apaga todos os serviços atuais e recria a lista a partir do novo arquivo.
          </DialogContentText>
        </Stack>
      </DialogDefaultV2>
      <PdfViewerDialogV2
        open={pdfOpen}
        onClose={() => setPdfOpen(false)}
        file={pdfFile}
        title={pdfNome}
      />
    </WidgetV2>
  );
};

ParseRfgPanel.propTypes = {
  nsirId: PropTypes.string,
  nsirStatus: PropTypes.string,
  canUpload: PropTypes.bool,
  job: PropTypes.object,
  documento: PropTypes.object,
  uploading: PropTypes.bool,
  conflict: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  startParseRfg: PropTypes.func.isRequired,
  fetchParseRfgJob: PropTypes.func.isRequired,
  fetchParseRfgDocumento: PropTypes.func.isRequired,
  clearParseRfgConflict: PropTypes.func.isRequired,
  messageError: PropTypes.func.isRequired,
};

ParseRfgPanel.defaultProps = {
  nsirId: null,
  nsirStatus: null,
  canUpload: false,
  job: null,
  documento: null,
  uploading: false,
  conflict: null,
};

const mapStateToProps = (state) => ({
  job: selectorsParseRfg.getParseRfgJob(state),
  uploading: selectorsParseRfg.getParseRfgUploading(state),
  documento: selectorsParseRfg.getParseRfgDocumento(state),
  conflict: selectorsParseRfg.getParseRfgConflict(state),
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators({ ...actionsParseRfg, ...actionsToast }, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(ParseRfgPanel);
